// API client for the Assessment payments dashboard
// (/api/assessment/admin/payments).
//
// Envelope: { data, isError, statusCode, errorMessage }. `data` is
//   { totalRevenue, thisMonthRevenue, activeSubscriptions,
//     failedTransactionsThisMonth, availableMethods: string[],
//     transactions: { content, page, size, totalElements, totalPages, hasNext } }
// Amounts are in rupees (not paise). Transactions are category AND test-series
// entitlements that ended ACTIVE ("Success"), FAILED ("Failed") or were refunded
// ("Refunded" / "Partially refunded"), newest status change first. Revenue figures
// are net of refunds. Filters (categoryId, method, status, type) narrow the
// transactions list and the CSV export only; the four summary figures are always
// global. There is no sort parameter.
import api from "../axiosInstance";

export { apiErrorMessage } from "./assessmentHierarchy";
export { listCategories } from "./assessmentCategories";

const BASE = "/api/assessment/admin/payments";

const unwrap = (res) => res.data?.data;

// Drop empty filters so they are not sent as `?method=`.
const cleanFilters = ({ categoryId, method, status, type } = {}) =>
  Object.fromEntries(
    Object.entries({ categoryId, method, status, type }).filter(
      ([, v]) => v !== undefined && v !== null && v !== "",
    ),
  );

// -> { totalRevenue, thisMonthRevenue, activeSubscriptions,
//      failedTransactionsThisMonth, availableMethods, transactions: page of
//      { studentName, itemType ("CATEGORY" | "SERIES"), itemName, categoryName
//        (category rows only), amount, refundedAmount, date, method, status } }
// filters: categoryId (category rows only), method, status ("Success" | "Failed" |
// "Refunded"), type ("category" | "series"). type=series cannot be combined with
// categoryId (the backend answers 400).
export const getPaymentsDashboard = ({ page = 0, size = 20, ...filters } = {}) =>
  api.get(BASE, { params: { ...cleanFilters(filters), page, size } }).then(unwrap);

// -> page { content, page, size, totalElements, totalPages, hasNext } of the mail/WhatsApp messages
// that FAILED or were SKIPPED (no email/phone, template not configured, source record gone), newest
// first: { id, eventType, channel, studentUserId, refKind, refId, status, attempts, lastError,
// createdAt, sentAt }. The backend clamps size to 1..100.
export const listNotificationProblems = ({ page = 0, size = 10 } = {}) =>
  api.get(`${BASE}/notifications`, { params: { page, size } }).then(unwrap);

const filenameFromDisposition = (disposition) => {
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition || "");
  return match ? decodeURIComponent(match[1]) : null;
};

// Downloads every matching transaction as CSV (not just the current page),
// honouring the same filters as the list. Columns: Student Name, Item Type,
// Item Name, Amount, Refunded Amount, Date, Method, Status. Resolves with the
// saved filename.
export const exportPaymentsCsv = async (filters = {}) => {
  const res = await api.get(`${BASE}/export`, { params: cleanFilters(filters), responseType: "blob" });
  const filename =
    filenameFromDisposition(res.headers?.["content-disposition"]) ||
    `assessment-payments-${new Date().toISOString().slice(0, 10)}.csv`;
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
  return filename;
};
